import { getMyApplications, getPrograms } from '@/containers/My/_api'
import ProgressBadge from '@/containers/My/_component/ProgramItem/ProgressBadge'
import { IMyApplications, IMyPrograms } from '@/containers/My/_type'
import { useAuthStore } from '@/lib/stores/authStore'
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useQuery } from '@tanstack/react-query'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import OutlineButton from '../Button/OutlineButton'
import { useMenuItems } from '@/lib/hooks/useMenuItems'
import { useEffect } from 'react'
import { useCompanyContext } from '@/lib/providers/CompanyProvider'
import { useTranslation } from 'react-i18next'

const MyMenuMobile = ({ setIsOpenDropdown }: { setIsOpenDropdown: (isOpen: boolean) => void }) => {
  const router = useRouter()
  const { user } = useAuthStore()
  const { type } = useCompanyContext()
  const { t } = useTranslation('common')

  const handleLogout = () => {
    router.push('/login')
  }

  const { data: myApplications, isFetched: isMyApplicationsFetched } = useQuery({
    queryKey: ['myApplications'],
    queryFn: () =>
      getMyApplications({
        page: 1,
        limit: 2,
        filters: `status:pending,submitted`,
        sort: '-created_at',
        application_status: 'ing',
      }),
    select: data => data.data,
    // no cache
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })

  const { data: myPrograms, isFetched: isMyProgramsFetched } = useQuery({
    queryKey: ['myPrograms'],
    queryFn: () => getPrograms({ page: 1, limit: 2, filters: `status:progress` }),
    select: data => data.data,
    // no cache
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [])

  return (
    <div className="fixed right-0 top-0 h-full w-full bg-white">
      <div className="flex flex-col">
        {/* head */}
        <div className="flex h-[56px] w-full items-center justify-end px-5">
          <button onClick={() => setIsOpenDropdown(false)}>
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        {/* body 1 */}
        <div className="flex flex-col gap-5 p-4 pb-5">
          <div className="flex flex-col gap-1">
            <div className="text-lg font-semibold text-label-strong">
              {t('mymenuLine.user', { name: user?.name || '' })}
            </div>
            <div className="text-sm text-neutral">{user?.email}</div>
          </div>
          <div className="flex w-full flex-row gap-2">
            {useMenuItems().map(item => {
              const { id, Icon, title, href } = item
              return (
                <Link
                  href={href}
                  key={`header-menu-mobile-${id}`}
                  className="flex-1"
                  onClick={() => setIsOpenDropdown(false)}
                >
                  <div
                    className="flex h-[88px] flex-col items-center justify-center gap-1.5 rounded-xl"
                    style={{
                      backgroundColor: 'color-mix(in srgb, var(--Primary-Normal) 5%, transparent)',
                    }}
                  >
                    <Icon className="h-[28px] w-[28px] text-primary-normal" />
                    <div className="text-sm text-primary-normal">{title}</div>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>

        {/* divider */}
        <div className="h-2 bg-strong"></div>

        {/* 신청중인 지원서 */}
        {(type === 'allinone' || type === 'recruit') && (
          <div className="flex flex-col gap-3 p-4 pb-1">
            <div className="text-sm text-neutral">{t('mymenu.inProgressApplications')}</div>
            {isMyApplicationsFetched && (
              <>
                {myApplications && myApplications.length > 0 ? (
                  <>
                    {myApplications.map((application: IMyApplications) => (
                      <Link href={`/${application.program_id}/apply`} key={application.id}>
                        <div className="flex h-[52px] flex-row items-center gap-1.5 rounded-xl border border-gray-800 px-3">
                          <div className="line-clamp-1 flex-1">{application.program_name}</div>
                          <img
                            src={'/svg/icon_solid_cheveron_right.svg'}
                            alt="chevron-right"
                            className="h-[28px] w-[28px]"
                          />
                        </div>
                      </Link>
                    ))}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2.5 rounded-2xl bg-alternative px-3 py-5">
                    <div className="text-sm text-label-alternative">
                      {t('mymenu.noApplications')}
                    </div>
                    <div>
                      <Link href="/">
                        <OutlineButton size="md" variant="assistive" className="!bg-white">
                          {t('mymenu.goBrowse')}
                        </OutlineButton>
                      </Link>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* 과정 목록 */}
        {(type === 'allinone' || type === 'lecture') && (
          <div className="flex flex-col gap-3 p-5">
            <div className="text-sm text-neutral">{t('mymenu.browseCourses')}</div>
            {isMyProgramsFetched && (
              <>
                {myPrograms && myPrograms.length > 0 ? (
                  <>
                    {myPrograms.map((program: IMyPrograms) => (
                      <Link href={`/my/programs/${program.code}/board`} key={program.id}>
                        <div className="flex h-[80px] flex-row items-center gap-1.5 rounded-xl bg-[#F7F7F8] px-3">
                          <div className="flex flex-1 flex-col gap-2">
                            <div className="flex">
                              <ProgressBadge status={program.status} />
                            </div>
                            <div className="line-clamp-1">{program.name}</div>
                          </div>
                          <img
                            src={'/svg/icon_solid_cheveron_right.svg'}
                            alt="chevron-right"
                            className="h-[28px] w-[28px]"
                          />
                        </div>
                      </Link>
                    ))}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2.5 rounded-2xl bg-alternative px-3 py-5">
                    <div className="text-sm text-label-alternative">{t('mymenu.noPrograms')}</div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* footer */}
        <div className="flex flex-col gap-2 p-5">
          <button
            className="flex text-sm font-normal text-neutral underline"
            onClick={handleLogout}
          >
            {t('auth.logout')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default MyMenuMobile
